import React, { useEffect, useRef } from 'react';
import { DETAILED_DEMOS } from '../data/heroDemoScenarios';
import './ChatDemo.css';

const DEFAULT_DEMOS = [
  {
    question: 'How do I set up absence plans in Oracle HCM?',
    answer: [
      'Navigate to **Setup and Maintenance → Absence Management → Absence Plans**.',
      '',
      'Click **Create** and configure:',
      '• Plan type (Accrual, Compensatory, or No Accrual)',
      '• Eligibility rules and waiting periods',
      '• Accrual frequency and carryover limits',
      '',
      '💡 Oracle HCM 25D',
    ],
  },
  {
    question: 'What changed in Oracle HCM 25D for payroll?',
    answer: [
      'Key **Oracle HCM 25D** payroll updates:',
      '',
      '• Enhanced **retroactive pay** processing rules',
      '• Updated **statutory compliance** for CA, UK, India',
      '• New **payroll flow** monitoring dashboard',
      '• Improved element entry **mass update** capabilities',
      '',
      '💡 Oracle HCM 25D',
    ],
  },
  {
    question: 'How do I configure Canadian T4 reporting?',
    answer: [
      'For Canadian T4 reporting, navigate to:',
      '**Setup and Maintenance → Legal Entity → Legal Reporting**',
      '',
      'Configure your **Tax Reporting Units** per province:',
      '• Set Business Number (BN) for CRA reporting',
      '• Map provincial tax accounts (ON, BC, AB, etc.)',
      '• Enable **T4 slip generation** in year-end tasks',
      '',
      '💡 Oracle HCM 25C',
    ],
  },
];

const formatLine = (line) => line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

const AnimatedChatDemo = ({ demos = DEFAULT_DEMOS, compact = false, detailed = false }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      if (detailed) {
        const demo = DETAILED_DEMOS[0];
        container.innerHTML = `
          <div class="demo-msg user">
            <span class="demo-msg-text">${demo.question}</span>
          </div>
          <div class="retrieval-panel">
            <div class="retrieval-header">Searching knowledge base...</div>
            ${demo.retrieval.map(row => `
              <div class="retrieval-row" style="opacity: 1; animation: none;">
                <span class="retrieval-filename">${row.file}</span>
                <div class="retrieval-bar-track">
                  <div class="retrieval-bar-fill" style="transform: scaleX(${row.score / 100}); transition: none;"></div>
                </div>
                <span class="retrieval-score">${row.score}%</span>
                <span class="retrieval-status ${row.verified ? 'status-verified' : 'status-good'}">${row.verified ? '✓ Verified' : 'Good match'}</span>
              </div>
            `).join('')}
          </div>
          <div class="demo-msg bot">
            <div class="demo-bot-avatar">
              <img src="/StratusEdgeFinal_whiteupscaled.png" alt="Edge AI" />
            </div>
            <div class="demo-bot-body">
              ${demo.answer.map(line => {
                if (line === '') return '<br/>';
                if (line.startsWith('💡')) return `<span class="demo-release-badge">${line.replace('💡 ', '')}</span>`;
                return `<p>${formatLine(line)}</p>`;
              }).join('')}
            </div>
          </div>
        `;
      } else {
        const demo = demos[0];
        container.innerHTML = `
          <div class="demo-msg user">
            <span class="demo-msg-text">${demo.question}</span>
          </div>
          <div class="demo-msg bot">
            <div class="demo-bot-avatar">
              <img src="/StratusEdgeFinal_whiteupscaled.png" alt="Edge AI" />
            </div>
            <div class="demo-bot-body">
              ${demo.answer.map(line => {
                if (line === '') return '<br/>';
                if (line.startsWith('💡')) return `<span class="demo-release-badge">${line.replace('💡 ', '')}</span>`;
                return `<p>${formatLine(line)}</p>`;
              }).join('')}
            </div>
          </div>
        `;
      }
      return undefined;
    }

    if (detailed) {
      let currentDemo = 0;
      let currentChar = 0;
      let currentLine = 0;
      let demoState = 'typing-q';
      let isPausing = false;
      let animationTimeout;

      const animateDetailed = () => {
        const demo = DETAILED_DEMOS[currentDemo];

        if (demoState === 'typing-q') {
          if (currentChar < demo.question.length) {
            currentChar++;
            container.innerHTML = `
              <div class="demo-msg user">
                <span class="demo-msg-text">${demo.question.substring(0, currentChar)}<span class="demo-cursor visible">|</span></span>
              </div>
            `;
            animationTimeout = setTimeout(animateDetailed, 40);
          } else {
            demoState = 'retrieving';
            container.innerHTML = `
              <div class="demo-msg user">
                <span class="demo-msg-text">${demo.question}</span>
              </div>
              <div class="retrieval-panel">
                <div class="retrieval-header">Searching knowledge base...</div>
                ${demo.retrieval.map((row, i) => `
                  <div class="retrieval-row" style="animation-delay: ${i * 80}ms">
                    <span class="retrieval-filename">${row.file}</span>
                    <div class="retrieval-bar-track">
                      <div class="retrieval-bar-fill" data-score="${row.score}" style="transform: scaleX(0)"></div>
                    </div>
                    <span class="retrieval-score">${row.score}%</span>
                    <span class="retrieval-status ${row.verified ? 'status-verified' : 'status-good'}">${row.verified ? '✓ Verified' : 'Good match'}</span>
                  </div>
                `).join('')}
              </div>
            `;
            animationTimeout = setTimeout(() => {
              container.querySelectorAll('.retrieval-bar-fill').forEach(bar => {
                bar.style.transform = `scaleX(${bar.dataset.score / 100})`;
              });
              animationTimeout = setTimeout(() => {
                const panel = container.querySelector('.retrieval-panel');
                if (panel) panel.classList.add('retrieval-fading');
                animationTimeout = setTimeout(() => {
                  demoState = 'typing-a';
                  currentLine = 0;
                  animateDetailed();
                }, 300);
              }, 1800);
            }, 50);
          }
        } else if (demoState === 'typing-a') {
          const renderAnswer = () => {
            container.innerHTML = `
              <div class="demo-msg user">
                <span class="demo-msg-text">${demo.question}</span>
              </div>
              <div class="demo-msg bot">
                <div class="demo-bot-avatar">
                  <img src="/StratusEdgeFinal_whiteupscaled.png" alt="Edge AI" />
                </div>
                <div class="demo-bot-body">
                  ${demo.answer.slice(0, currentLine + 1).map((line, i) => {
                    if (line === '') return '<br/>';
                    if (line.startsWith('💡')) return `<span class="demo-release-badge">${line.replace('💡 ', '')}</span>`;
                    const isLast = i === currentLine;
                    return `<p>${formatLine(line)}${isLast && !isPausing ? '<span class="demo-cursor visible">|</span>' : ''}</p>`;
                  }).join('')}
                </div>
              </div>
            `;
          };
          if (currentLine < demo.answer.length - 1) {
            renderAnswer();
            currentLine++;
            animationTimeout = setTimeout(animateDetailed, 120);
          } else {
            isPausing = true;
            renderAnswer();
            animationTimeout = setTimeout(() => {
              currentDemo = (currentDemo + 1) % DETAILED_DEMOS.length;
              currentChar = 0;
              currentLine = 0;
              demoState = 'typing-q';
              isPausing = false;
              animateDetailed();
            }, 3000);
          }
        }
      };

      animationTimeout = setTimeout(animateDetailed, 800);
      return () => { if (animationTimeout) clearTimeout(animationTimeout); };
    }

    let currentDemo = 0;
    let currentLine = 0;
    let currentChar = 0;
    let isTypingQuestion = true;
    let isPausing = false;
    let animationTimeout;

    const renderDemo = () => {
      const demo = demos[currentDemo];
      container.innerHTML = `
        <div class="demo-msg user">
          <span class="demo-msg-text">${
            isTypingQuestion
              ? demo.question.substring(0, currentChar)
              : demo.question
          }<span class="demo-cursor ${isTypingQuestion ? 'visible' : 'hidden'}">|</span></span>
        </div>
        ${!isTypingQuestion ? `
          <div class="demo-msg bot">
            <div class="demo-bot-avatar">
              <img src="/StratusEdgeFinal_whiteupscaled.png" alt="Edge AI" />
            </div>
            <div class="demo-bot-body">
              ${demo.answer.slice(0, currentLine + 1).map((line, i) => {
                if (line === '') return '<br/>';
                if (line.startsWith('💡')) {
                  return `<span class="demo-release-badge">${line.replace('💡 ', '')}</span>`;
                }
                const isLastLine = i === currentLine;
                return `<p>${formatLine(line)}${
                  isLastLine && !isPausing ? '<span class="demo-cursor visible">|</span>' : ''
                }</p>`;
              }).join('')}
            </div>
          </div>
        ` : ''}
      `;
    };

    const animate = () => {
      const demo = demos[currentDemo];

      if (isTypingQuestion) {
        if (currentChar < demo.question.length) {
          currentChar += 1;
          renderDemo();
          animationTimeout = setTimeout(animate, 40);
        } else {
          isPausing = true;
          renderDemo();
          animationTimeout = setTimeout(() => {
            isPausing = false;
            isTypingQuestion = false;
            currentLine = 0;
            animate();
          }, 600);
        }
      } else if (currentLine < demo.answer.length - 1) {
        currentLine += 1;
        renderDemo();
        animationTimeout = setTimeout(animate, 120);
      } else {
        isPausing = true;
        renderDemo();
        animationTimeout = setTimeout(() => {
          currentDemo = (currentDemo + 1) % demos.length;
          currentChar = 0;
          currentLine = 0;
          isTypingQuestion = true;
          isPausing = false;
          animate();
        }, 4000);
      }
    };

    animationTimeout = setTimeout(animate, 800);

    return () => {
      if (animationTimeout) clearTimeout(animationTimeout);
    };
  }, [demos, detailed]);

  return (
    <div
      ref={containerRef}
      className={`demo-content${compact ? ' demo-content-compact' : ''}`}
    />
  );
};

export default AnimatedChatDemo;
